import React, { useState, useEffect, useRef } from 'react';
import { User, Building, Settings, ChevronDown, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { patientService } from '../services/patientService';

const PatientPreferencesPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient Admin';
  }, []);

  // Edit states for different sections
  const [editingSections, setEditingSections] = useState({
    doctor: false,
    clinic: false,
    settings: false
  });
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [notificationsDropdownOpen, setNotificationsDropdownOpen] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const notificationsDropdownRef = useRef(null);

  // Doctor dropdown states
  const [allDoctors, setAllDoctors] = useState([]);
  const [doctorDropdownOpen, setDoctorDropdownOpen] = useState(false);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState('');
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const doctorDropdownRef = useRef(null);

  // Clinic dropdown states
  const [availableClinics, setAvailableClinics] = useState([]);
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const clinicDropdownRef = useRef(null);
  
  // Store original data for cancel functionality
  const [originalData, setOriginalData] = useState(null);
  
  const [preferences, setPreferences] = useState({
    doctor: {
      name: '',
      specialties: '',
      summary: '',
      doctor_id: ''
    },
    clinic: {
      name: '',
      address: '',
      clinic_id: null
    },
    settings: {
      country: '',
      language: '',
      currency: '',
      notifications: ''
    }
  });

  // Fetch preferences data on component mount
  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setLoadingDetails(true);
      setError('');
      const data = await patientService.getPatientPreferences();
      
      if (data) {
        let doctorDetails = { specialties: '', summary: '' };
        let clinicDetails = { address: '' };
        
        // Fetch doctor details if doctor_id exists (numeric ID)
        if (data.doctor_id) {
          try {
            const doctorData = await patientService.getDoctorByNumericId(data.doctor_id);
            if (doctorData) {
              doctorDetails = {
                specialties: doctorData.specialties || '',
                summary: doctorData.summary || '',
                doctor_id: doctorData.doctor_id  // Store the string doctor_id for UI
              };
            }
          } catch (doctorError) {
            console.error('Error fetching doctor details:', doctorError);
            // Keep empty values if doctor details can't be fetched
            doctorDetails = { specialties: '', summary: '', doctor_id: '' };
          }
        }
        
        // Fetch clinic details if clinic_id exists
        if (data.clinic_id) {
          try {
            const clinicData = await patientService.getClinicById(data.clinic_id);
            if (clinicData) {
              clinicDetails = {
                address: clinicData.clinic_address || ''
              };
            }
          } catch (clinicError) {
            console.error('Error fetching clinic details:', clinicError);
            // Keep empty values if clinic details can't be fetched
            clinicDetails = { address: '' };
          }
        }
        
        const prefsData = {
          doctor: {
            name: data.doctor_name || '',
            specialties: doctorDetails.specialties,
            summary: doctorDetails.summary,
            doctor_id: doctorDetails.doctor_id || ''
          },
          clinic: {
            name: data.clinic_name || '',
            address: clinicDetails.address,
            clinic_id: data.clinic_id || null
          },
          settings: {
            country: data.country || '',
            language: data.language || '',
            currency: data.currency || '',
            notifications: data.notifications || ''
          }
        };
        
        setPreferences(prefsData);
        setOriginalData(prefsData);
        
        // Set notifications array
        if (data.notifications) {
          setSelectedNotifications(data.notifications.split(',').map(n => n.trim()));
        }
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setError('Failed to fetch preferences. Using default values.');
      // Keep empty default values if no preferences exist
    } finally {
      setLoading(false);
      setLoadingDetails(false);
    }
  };

  const handleEditRecord = (section) => {
    setEditingSections(prev => ({
      ...prev,
      [section]: true
    }));
    // Store original data for cancel functionality
    setOriginalData({ ...preferences });
  };

  const handleSave = async (section) => {
    try {
      setSaving(true);
      setError('');
      
      // Get the numeric doctor ID from the string doctor_id
      let numericDoctorId = null;
      if (preferences.doctor.doctor_id) {
        try {
          const doctorDetail = await patientService.getDoctorById(preferences.doctor.doctor_id);
          numericDoctorId = doctorDetail.id;
        } catch (error) {
          console.error('Error fetching doctor ID:', error);
        }
      }
      
      // Prepare data for API
      const apiData = {
        doctor_name: preferences.doctor.name,
        doctor_id: numericDoctorId,
        clinic_name: preferences.clinic.name,
        clinic_id: preferences.clinic.clinic_id,
        country: preferences.settings.country,
        language: preferences.settings.language,
        currency: preferences.settings.currency,
        notifications: selectedNotifications.join(',')
      };

      // Try to update first, if it fails then create
      try {
        await patientService.updatePatientPreferences(apiData);
      } catch (updateError) {
        // If update fails (record doesn't exist), try to create
        await patientService.createPatientPreferences(apiData);
      }

      setEditingSections(prev => ({
        ...prev,
        [section]: false
      }));
      
      setSuccessMessage('Preferences updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error saving preferences:', error);
      setError('Failed to save preferences. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (section) => {
    // Restore original data
    if (originalData) {
      setPreferences(originalData);
      if (originalData.settings.notifications) {
        setSelectedNotifications(originalData.settings.notifications.split(',').map(n => n.trim()));
      }
    }
    
    setEditingSections(prev => ({
      ...prev,
      [section]: false
    }));
    setError('');
  };

  const handleInputChange = (section, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNotificationToggle = (notification) => {
    setSelectedNotifications(prev => {
      if (prev.includes(notification)) {
        return prev.filter(n => n !== notification);
      } else {
        return [...prev, notification];
      }
    });
    // Don't close the dropdown when toggling options
  };

  const handleRemoveNotification = (notification) => {
    setSelectedNotifications(prev => prev.filter(n => n !== notification));
  };

  const toggleNotificationsDropdown = () => {
    setNotificationsDropdownOpen(!notificationsDropdownOpen);
  };

  // Fetch all doctors for dropdown
  const fetchAllDoctors = async () => {
    try {
      setLoadingDoctors(true);
      const doctors = await patientService.getAllDoctors();
      setAllDoctors(doctors);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      setError('Failed to fetch doctors list');
    } finally {
      setLoadingDoctors(false);
    }
  };

  // Handle doctor selection
  const handleDoctorSelect = async (doctor) => {
    try {
      setLoadingClinics(true);
      
      // Update doctor information
      setPreferences(prev => ({
        ...prev,
        doctor: {
          name: `${doctor.firstname} ${doctor.lastname}`,
          specialties: doctor.specialties || '',
          summary: doctor.summary || '',
          doctor_id: doctor.doctor_id
        }
      }));

      // Get clinics for this doctor
      const detail = await patientService.getDoctorById(doctor.doctor_id);
      const clinics = await patientService.getClinicsByDoctor(detail.id);
      setAvailableClinics(clinics);

      // Find primary clinic and set as default
      const primaryClinic = clinics.find(clinic => clinic.is_primary_clinic);
      if (primaryClinic) {
        setPreferences(prev => ({
          ...prev,
          clinic: {
            name: primaryClinic.clinic_name,
            address: primaryClinic.clinic_address,
            clinic_id: primaryClinic.clinic_id
          }
        }));
      } else if (clinics.length > 0) {
        // If no primary clinic, use the first one
        setPreferences(prev => ({
          ...prev,
          clinic: {
            name: clinics[0].clinic_name,
            address: clinics[0].clinic_address,
            clinic_id: clinics[0].clinic_id
          }
        }));
      }

      setDoctorDropdownOpen(false);
      setDoctorSearchTerm('');
    } catch (error) {
      console.error('Error selecting doctor:', error);
      setError('Failed to load doctor information');
    } finally {
      setLoadingClinics(false);
    }
  };

  // Handle clinic selection
  const handleClinicSelect = (clinic) => {
    setPreferences(prev => ({
      ...prev,
      clinic: {
        name: clinic.clinic_name,
        address: clinic.clinic_address,
        clinic_id: clinic.clinic_id
      }
    }));
    setClinicDropdownOpen(false);
  };

  // Toggle doctor dropdown
  const toggleDoctorDropdown = () => {
    if (!doctorDropdownOpen && allDoctors.length === 0) {
      fetchAllDoctors();
    }
    setDoctorDropdownOpen(!doctorDropdownOpen);
  };

  // Toggle clinic dropdown
  const toggleClinicDropdown = () => {
    setClinicDropdownOpen(!clinicDropdownOpen);
  };

  // Filter doctors based on search term
  const filteredDoctors = allDoctors.filter(doctor => 
    `${doctor.firstname} ${doctor.lastname}`.toLowerCase().includes(doctorSearchTerm.toLowerCase()) ||
    (doctor.specialties && doctor.specialties.toLowerCase().includes(doctorSearchTerm.toLowerCase()))
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsDropdownRef.current && !notificationsDropdownRef.current.contains(event.target)) {
        setNotificationsDropdownOpen(false);
      }
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
        setDoctorDropdownOpen(false);
      }
      if (clinicDropdownRef.current && !clinicDropdownRef.current.contains(event.target)) {
        setClinicDropdownOpen(false);
      }
    };

    if (notificationsDropdownOpen || doctorDropdownOpen || clinicDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [notificationsDropdownOpen, doctorDropdownOpen, clinicDropdownOpen]);

  if (loading) {
    return (
      <div className="flex gap-6">
        <div className="w-1/5 bg-gray-100 min-h-screen p-4">
          <div className="space-y-2">
            <div className="bg-gray-200 h-12 rounded-md animate-pulse"></div>
            <div className="bg-blue-600 h-12 rounded-md"></div>
            <div className="bg-gray-200 h-12 rounded-md animate-pulse"></div>
            <div className="bg-gray-200 h-12 rounded-md animate-pulse"></div>
          </div>
        </div>
        <div className="w-4/5 bg-gray-100 p-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading preferences...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg shadow-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        </div>
      )}
      
      {error && (
        <div className="fixed top-4 right-4 z-50 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg shadow-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L10 9.414l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

              {/* Left Column - 20% */}
        <div className="w-1/5 bg-gray-100 min-h-screen p-4">
          <div className="space-y-2">
            <Link
              to="/patient/admin/mydetails"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              My Details
            </Link>
            <button
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-blue-600 text-white h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] shadow-md"
            >
              Preferences
            </button>
            <Link
              to="/patient/admin/accounts"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Accounts
            </Link>
            <Link
              to="/patient/admin/bookings"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Bookings
            </Link>
          </div>
        </div>

      {/* Right Column - 80% */}
      <div className="w-4/5 bg-gray-100 p-4 space-y-4">
        {/* Preferred Doctor Block */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center mb-4">
            <User className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-800">Preferred Doctor</h2>
          </div>
          <div className="border-b border-blue-200 mb-4"></div>
          
          <div className="flex gap-4 mb-4">
            <div className="w-[12%]">
              <img
                src="/doctor_logo.jpg"
                alt="Doctor Logo"
                className="w-full h-12 object-contain mt-[10px]"
              />
            </div>
            <div className="w-[20%]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Doctor Name:
              </label>
              <div className="relative" ref={doctorDropdownRef}>
                <div
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${
                    editingSections.doctor ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                  }`}
                  onClick={editingSections.doctor ? toggleDoctorDropdown : undefined}
                >
                  <div className="flex items-center justify-between">
                    <span className={`${preferences.doctor.name ? 'text-gray-900' : 'text-gray-400'}`}>
                      {preferences.doctor.name || 'Select Preferred Doctor'}
                    </span>
                    {editingSections.doctor && (
                      <ChevronDown className={`h-4 w-4 transition-transform ${doctorDropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </div>

                {/* Doctor Dropdown */}
                {doctorDropdownOpen && editingSections.doctor && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                        placeholder="Search doctors..."
                        value={doctorSearchTerm}
                        onChange={(e) => setDoctorSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* Doctor List */}
                    <div className="max-h-48 overflow-y-auto">
                      {loadingDoctors ? (
                        <div className="px-3 py-2 text-center text-gray-500">Loading doctors...</div>
                      ) : filteredDoctors.length > 0 ? (
                        filteredDoctors.map((doctor) => (
                          <div
                            key={doctor.id}
                            className="px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                            onClick={() => handleDoctorSelect(doctor)}
                          >
                            <div className="font-medium text-gray-900">
                              {doctor.firstname} {doctor.lastname}
                            </div>
                            <div className="text-xs text-gray-600">
                              {doctor.specialties || 'General Practice'}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-center text-gray-500">
                          {doctorSearchTerm ? 'No doctors found' : 'No doctors available'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="w-[20%]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialties:
              </label>
              <input
                type="text"
                value={loadingDetails ? 'Loading...' : preferences.doctor.specialties}
                onChange={(e) => handleInputChange('doctor', 'specialties', e.target.value)}
                placeholder="Preferred Doctor Specialties"
                readOnly={!editingSections.doctor || loadingDetails}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingSections.doctor && !loadingDetails ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
            <div className="w-[40%]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Summary:
              </label>
              <input
                type="text"
                value={loadingDetails ? 'Loading...' : preferences.doctor.summary}
                onChange={(e) => handleInputChange('doctor', 'summary', e.target.value)}
                placeholder="Preferred Doctor Summary"
                readOnly={!editingSections.doctor || loadingDetails}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingSections.doctor && !loadingDetails ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            {!editingSections.doctor ? (
            <button
              onClick={() => handleEditRecord('doctor')}
              className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
            >
              Change Doctor
            </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancel('doctor')}
                  className="bg-gray-500 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-600 active:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave('doctor')}
                  disabled={saving}
                  className="bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Preferred Clinic Block */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center mb-4">
            <Building className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-800">Preferred Clinic</h2>
          </div>
          <div className="border-b border-blue-200 mb-4"></div>
          
          <div className="flex gap-4 mb-4">
            <div className="w-[12%]">
              <img
                src="/clinic_logo.png"
                alt="Clinic Logo"
                className="w-full h-12 object-contain mt-[10px]"
              />
            </div>
            <div className="w-[40%]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic Name:
              </label>
              <div className="relative" ref={clinicDropdownRef}>
                <div
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${
                    editingSections.clinic ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                  }`}
                  onClick={editingSections.clinic && availableClinics.length > 0 ? toggleClinicDropdown : undefined}
                >
                  <div className="flex items-center justify-between">
                    <span className={`${preferences.clinic.name ? 'text-gray-900' : 'text-gray-400'}`}>
                      {preferences.clinic.name || 'Select Preferred Clinic'}
                    </span>
                    {editingSections.clinic && availableClinics.length > 0 && (
                      <ChevronDown className={`h-4 w-4 transition-transform ${clinicDropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </div>

                {/* Clinic Dropdown */}
                {clinicDropdownOpen && editingSections.clinic && availableClinics.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {availableClinics.map((clinic) => (
                      <div
                        key={clinic.clinic_id}
                        className="px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                        onClick={() => handleClinicSelect(clinic)}
                      >
                        <div className="font-medium text-gray-900">
                          {clinic.clinic_name}
                        </div>
                        <div className="text-xs text-gray-600">
                          {clinic.clinic_address}
                          {clinic.is_primary_clinic && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Primary
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {editingSections.clinic && availableClinics.length === 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Please select a doctor first to see available clinics
                  </div>
                )}
              </div>
            </div>
            <div className="w-[40%]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic Address:
              </label>
              <input
                type="text"
                value={loadingDetails ? 'Loading...' : preferences.clinic.address}
                onChange={(e) => handleInputChange('clinic', 'address', e.target.value)}
                placeholder="Preferred Clinic Address"
                readOnly={!editingSections.clinic || loadingDetails}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingSections.clinic && !loadingDetails ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            {!editingSections.clinic ? (
            <button
              onClick={() => handleEditRecord('clinic')}
              className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
            >
              Change Clinic
            </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancel('clinic')}
                  className="bg-gray-500 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-600 active:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave('clinic')}
                  disabled={saving}
                  className="bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Settings Block */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center mb-4">
            <Settings className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-800">Settings</h2>
          </div>
          <div className="border-b border-blue-200 mb-4"></div>
          
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country:
              </label>
              <input
                type="text"
                value={preferences.settings.country}
                onChange={(e) => handleInputChange('settings', 'country', e.target.value)}
                placeholder="Preferred Country"
                readOnly={!editingSections.settings}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingSections.settings ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language:
              </label>
              <input
                type="text"
                value={preferences.settings.language}
                onChange={(e) => handleInputChange('settings', 'language', e.target.value)}
                placeholder="Preferred Language"
                readOnly={!editingSections.settings}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingSections.settings ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency:
              </label>
              <input
                type="text"
                value={preferences.settings.currency}
                onChange={(e) => handleInputChange('settings', 'currency', e.target.value)}
                placeholder="Preferred Currency"
                readOnly={!editingSections.settings}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingSections.settings ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notifications:
              </label>
              <div className="relative" ref={notificationsDropdownRef}>
                <div 
                  className={`w-full px-3 py-1 pr-8 border border-gray-300 rounded-md min-h-[32px] flex flex-wrap items-center gap-1 cursor-pointer ${
                    editingSections.settings ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                  }`}
                  onClick={editingSections.settings ? toggleNotificationsDropdown : undefined}
                >
                  {selectedNotifications.length > 0 ? (
                    selectedNotifications.map((notification, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {notification}
                        {editingSections.settings && (
                        <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveNotification(notification);
                            }}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">Select Notifications</span>
                  )}
                  {editingSections.settings && (
                    <ChevronDown className={`h-4 w-4 transition-transform ml-auto ${notificationsDropdownOpen ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </div>
              
              {/* Dropdown */}
              {notificationsDropdownOpen && editingSections.settings && (
                <div 
                  className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg"
                  style={{ minWidth: '200px' }}
                >
                    {['Email', 'SMS'].map((notification) => (
                    <div
                        key={notification}
                      className="flex items-center px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => {
                        handleNotificationToggle(notification);
                      }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(notification)}
                        onChange={() => {}}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      <span className="text-sm text-gray-700 font-medium">{notification}</span>
                    </div>
                    ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Settings Edit Button */}
          <div className="flex justify-end space-x-2 mt-4">
            {!editingSections.settings ? (
              <button
                onClick={() => handleEditRecord('settings')}
                className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
              >
                Edit Settings
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancel('settings')}
                  className="bg-gray-500 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-600 active:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave('settings')}
                  disabled={saving}
                  className="bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientPreferencesPage; 